import React, { useState } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import AssignmentIndIcon        from '@mui/icons-material/AssignmentInd';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DescriptionIcon          from '@mui/icons-material/Description';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import AccountBalanceIcon       from '@mui/icons-material/AccountBalance';
import VerifiedIcon             from '@mui/icons-material/Verified';
import CreditCardIcon           from '@mui/icons-material/CreditCard';
import ReceiptLongIcon          from '@mui/icons-material/ReceiptLong';
import { motion }               from 'framer-motion';
import SectionHeader            from './SectionHeader';
import ConsultationModal        from './ConsultationModal';
import { C, F, SH }             from '../styles/tokens';
import { btnBlueSx }            from '../styles/mixins';
import { Button }               from '@mui/material';
import ArrowForwardIcon         from '@mui/icons-material/ArrowForward';

const STEPS = [
  {
    label:    'Name Reservation',
    sub:      'RUN — MCA Portal',
    time:     '1–2 days',
    icon:     <DriveFileRenameOutlineIcon />,
    description: 'Reserve your unique company name via the RUN service. The name must comply with Companies Act, 2013 naming guidelines.',
    phase:    'foundation',
  },
  {
    label:    'Digital Signature Certificate',
    sub:      'Class 3 DSC',
    time:     '1–3 days',
    icon:     <AssignmentIndIcon />,
    description: 'All proposed directors obtain a Class 3 DSC from a certified authority to digitally sign MCA filings.',
    phase:    'foundation',
  },
  {
    label:    'Draft MOA & AOA',
    sub:      'Legal Documents',
    time:     '1–2 days',
    icon:     <DescriptionIcon />,
    description: 'Prepare the Memorandum of Association (company objectives) and Articles of Association (governance rules).',
    phase:    'documentation',
  },
  {
    label:    'File SPICe+ with MCA',
    sub:      'Integrated Form',
    time:     '2–3 days',
    icon:     <PublishedWithChangesIcon />,
    description: 'Single integrated form covering incorporation, DIN allotment, PAN, TAN, ESIC, EPFO, and GST registration.',
    phase:    'documentation',
  },
  {
    label:    'Certificate of Incorporation',
    sub:      'ROC Issues CIN',
    time:     '7–15 days',
    icon:     <VerifiedIcon />,
    description: 'The Registrar of Companies issues the Certificate of Incorporation with a unique Corporate Identity Number.',
    phase:    'registration',
  },
  {
    label:    'PAN, TAN & DIN Allotment',
    sub:      'Auto-allotted by MCA',
    time:     'Same day',
    icon:     <CreditCardIcon />,
    description: 'Permanent Account Number (PAN), Tax Deduction Account Number (TAN), and Director Identification Numbers (DIN) are issued automatically alongside the Certificate of Incorporation.',
    phase:    'registration',
  },
  {
    label:    'Open Current Bank Account',
    sub:      'Business Banking',
    time:     '3–5 days',
    icon:     <AccountBalanceIcon />,
    description: "Open a current account in the company's name using the CoI, MOA, AOA, and PAN. Deposit subscribed share capital.",
    phase:    'registration',
  },
  {
    label:    'GST & Other Registrations',
    sub:      'Go Live!',
    time:     '3–7 days',
    icon:     <ReceiptLongIcon />,
    description: 'Register for GST, MSME, Shop & Establishment Act, and any industry-specific licences. Your business is now fully operational.',
    phase:    'live',
  },
];

const PHASE_COLOR = {
  foundation:    C.blue,
  documentation: '#7C3AED',
  registration:  '#0891B2',
  live:          C.green,
};

const PHASE_LABEL = {
  foundation:    'Foundation',
  documentation: 'Documentation',
  registration:  'Registration',
  live:          'You\'re Live 🎉',
};

// ── Journey step card ──────────────────────────────────────────────────────────

const StepCard = ({ step, index, isLeft }) => {
  const color = PHASE_COLOR[step.phase];

  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: 0.05, ease: 'easeOut' }}
      style={{ flex: 1 }}
    >
      <Box
        sx={{
          backgroundColor: C.white,
          border:           `1px solid ${C.border}`,
          borderLeft:       `4px solid ${color}`,
          borderRadius:     '14px',
          boxShadow:        SH.card,
          p:                { xs: 2.5, md: 3 },
          transition:       'box-shadow 0.25s, transform 0.25s',
          '&:hover':        { boxShadow: SH.cardHover, transform: 'translateY(-3px)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width:           40,
              height:          40,
              borderRadius:    '10px',
              backgroundColor: `${color}15`,
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              color,
              flexShrink:      0,
            }}
          >
            {step.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
              <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: { xs: 13.5, md: 15 }, color: C.navy, lineHeight: 1.2 }}>
                {step.label}
              </Typography>
              <Typography sx={{ fontFamily: F.body, fontSize: 11, color, fontWeight: 600, backgroundColor: `${color}12`, px: 1, py: 0.25, borderRadius: '20px', whiteSpace: 'nowrap' }}>
                ⏱ {step.time}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: F.body, fontSize: 11.5, color: C.textMuted, mb: 0.75 }}>
              {step.sub}
            </Typography>
            <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMid, lineHeight: 1.65 }}>
              {step.description}
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

// ── Desktop alternating timeline ───────────────────────────────────────────────

const DesktopTimeline = () => (
  <Box sx={{ position: 'relative' }}>
    {/* Center spine */}
    <Box
      sx={{
        position:        'absolute',
        left:            '50%',
        top:             0,
        bottom:          0,
        width:           2,
        transform:       'translateX(-50%)',
        background:      `linear-gradient(to bottom, ${C.blue}, #7C3AED, #0891B2, ${C.green})`,
        borderRadius:    4,
        zIndex:          0,
      }}
    />

    {STEPS.map((step, idx) => {
      const isLeft  = idx % 2 === 0;
      const color   = PHASE_COLOR[step.phase];
      const isFirst = idx === 0 || STEPS[idx - 1].phase !== step.phase;

      return (
        <Box key={idx}>
          {/* Phase label (first step of each phase) */}
          {isFirst && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5, mt: idx === 0 ? 0 : 2, position: 'relative', zIndex: 1 }}>
                <Box sx={{ backgroundColor: color, color: C.white, fontFamily: F.heading, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', px: 2, py: 0.5, borderRadius: '20px' }}>
                  {PHASE_LABEL[step.phase]}
                </Box>
              </Box>
            </motion.div>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, position: 'relative', zIndex: 1 }}>
            {/* Left card or spacer */}
            <Box sx={{ flex: 1, pr: 3 }}>
              {isLeft && <StepCard step={step} index={idx} isLeft />}
            </Box>

            {/* Node */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 0.1, type: 'spring', stiffness: 200 }}
            >
              <Box
                sx={{
                  width:           36,
                  height:          36,
                  borderRadius:    '50%',
                  backgroundColor: color,
                  color:           C.white,
                  display:         'flex',
                  alignItems:      'center',
                  justifyContent:  'center',
                  fontFamily:      F.heading,
                  fontWeight:      700,
                  fontSize:        13,
                  flexShrink:      0,
                  boxShadow:       `0 0 0 4px ${color}30`,
                  zIndex:          2,
                  position:        'relative',
                }}
              >
                {idx + 1}
              </Box>
            </motion.div>

            {/* Right card or spacer */}
            <Box sx={{ flex: 1, pl: 3 }}>
              {!isLeft && <StepCard step={step} index={idx} isLeft={false} />}
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
);

// ── Mobile vertical timeline ───────────────────────────────────────────────────

const MobileTimeline = () => (
  <Box sx={{ position: 'relative', pl: 5 }}>
    {/* Left spine */}
    <Box
      sx={{
        position:     'absolute',
        left:         17,
        top:          0,
        bottom:       0,
        width:        2,
        background:   `linear-gradient(to bottom, ${C.blue}, #7C3AED, #0891B2, ${C.green})`,
        borderRadius: 4,
      }}
    />

    {STEPS.map((step, idx) => {
      const color = PHASE_COLOR[step.phase];
      return (
        <Box key={idx} sx={{ position: 'relative', mb: 2.5 }}>
          {/* Node */}
          <Box
            sx={{
              position:        'absolute',
              left:            -22,
              top:             12,
              width:           30,
              height:          30,
              borderRadius:    '50%',
              backgroundColor: color,
              color:           C.white,
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              fontFamily:      F.heading,
              fontWeight:      700,
              fontSize:        12,
              boxShadow:       `0 0 0 3px ${color}30`,
              zIndex:          1,
            }}
          >
            {idx + 1}
          </Box>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <StepCard step={step} index={idx} isLeft={false} />
          </motion.div>
        </Box>
      );
    })}
  </Box>
);

// ── Main component ─────────────────────────────────────────────────────────────

const AboutPracco = () => {
  const theme       = useTheme();
  const isMobile    = useMediaQuery(theme.breakpoints.down('md'));
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <Box id="about" sx={{ backgroundColor: C.bg, px: { xs: 2, md: 6 }, py: { xs: 6, md: 10 } }}>
      <SectionHeader
        label="Your Journey"
        title="From Idea to Incorporated"
        subtitle="A complete step-by-step path to registering your company in India — with typical timelines at every stage."
        mb={6}
      />

      {/* Phase legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mb: { xs: 4, md: 6 } }}>
        {Object.entries(PHASE_LABEL).map(([key, label]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: PHASE_COLOR[key] }} />
            <Typography sx={{ fontFamily: F.body, fontSize: 12.5, color: C.textMid, fontWeight: 500 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ maxWidth: '860px', mx: 'auto' }}>
        {isMobile ? <MobileTimeline /> : <DesktopTimeline />}

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, mb: 3 }}>
            Let our experts handle every step — so you can focus on building your business.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => setModalOpen(true)}
            sx={{ ...btnBlueSx, fontSize: 15, px: 5, py: 1.6 }}
          >
            Book Your Free Consultation
          </Button>
        </Box>
      </Box>

      <ConsultationModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </Box>
  );
};

export default AboutPracco;
